import { ButtonType, ButtonWidget, DialogPositioningBehavior, DialogType, DialogWidget } from '@tableau/widgets';
import React from 'react';

import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { GenerativeAIOnPremSectionStyles } from '~src/settings/general-settings/styles/tableau-gai/GenerativeAIOnPremSectionStyles';

import { Messages } from '../../../../../codegen/Localize';

export interface ErrorDetail {
  message: string;
  statusCode?: number;
  statusText?: string;
  provider?: string;
  timestamp: string;
  rawResponse?: string;
}

export function formatRawResponse(rawResponse: string): string {
  try {
    const reviver = (_key: string, value: unknown): unknown => {
      if (typeof value !== 'string') return value;
      const trimmed = value.trim();
      if (trimmed[0] !== '{' && trimmed[0] !== '[') return value;
      try {
        return JSON.parse(trimmed, reviver);
      } catch {
        return value;
      }
    };
    return JSON.stringify(JSON.parse(rawResponse, reviver), null, 2);
  } catch {
    return rawResponse;
  }
}

export function formatErrorDetails(errorDetail: ErrorDetail): string {
  const details: string[] = [];

  if (errorDetail.provider) {
    details.push(`${Messages.onPremGAIErrorDetailsProviderLabel()} ${errorDetail.provider}`);
  }

  if (errorDetail.statusCode) {
    details.push(`${Messages.onPremGAIErrorDetailsStatusCodeLabel()} ${errorDetail.statusCode} ${errorDetail.statusText || ''}`);
  }

  details.push(`${Messages.onPremGAIErrorDetailsErrorMessageLabel()} ${errorDetail.message}`);
  details.push(`${Messages.onPremGAIErrorDetailsTimestampLabel()} ${new Date(errorDetail.timestamp).toLocaleString()}`);

  if (errorDetail.rawResponse) {
    details.push(`\n${Messages.onPremGAIErrorDetailsRawResponseLabel()}\n${formatRawResponse(errorDetail.rawResponse)}`);
  }

  return details.join('\n\n');
}

const ErrorDetailsModal: React.FC<{ errorDetail: ErrorDetail, onClose: () => void }> = ({ errorDetail, onClose }) => {
  const footerContent = (
    <div className={GenerativeAIOnPremSectionStyles.errorDetailsModalFooter}>
      <ButtonWidget buttonType={ButtonType.Outline} onClick={onClose}>
        {Messages.ok()}
      </ButtonWidget>
    </div>
  );

  return (
    <DialogWidget
      title={Messages.onPremGAIErrorDetailsDialogTitle()}
      type={DialogType.NormalWithTitleBar}
      positioningBehavior={DialogPositioningBehavior.Centered}
      dialogWidth={TabDialogWidth.width375px}
      dialogHeight={GenerativeAIOnPremSectionStyles.ERROR_DETAILS_MODAL_HEIGHT}
      onCloseRequest={onClose}
      showCloseButton={true}
      showFooter={true}
      footerContent={footerContent}
      isModal={true}
    >
      <div className={GenerativeAIOnPremSectionStyles.errorDetailsModalBody}>
        <div className={GenerativeAIOnPremSectionStyles.errorDetailsModalMessage}>
          {Messages.onPremGAIErrorDetailsDialogMessage()}
        </div>
        <div className={GenerativeAIOnPremSectionStyles.errorDetailsModalLabel}>
          {Messages.onPremGAIErrorDetailsLabel()}
        </div>
        <div className={GenerativeAIOnPremSectionStyles.errorDetailsModalScrollArea}>
          <div className={GenerativeAIOnPremSectionStyles.errorDetailsModalText}>
            {formatErrorDetails(errorDetail)}
          </div>
        </div>
      </div>
    </DialogWidget>
  );
};

export function openErrorDetailsModal(errorDetail: ErrorDetail): void {
  const modalInstance = ReactModalService.openCustom(({ close }) => (
    <ErrorDetailsModal errorDetail={errorDetail} onClose={close} />
  ));

  modalInstance.result.catch(() => {});
}
